// The company on the page, split the way the sidebar splits it (Phase 4, item 6): a one-line
// identity strip above the tabs, then the sidebar's own Company, Outreach, Research and History
// tabs for a book account.
//
// A company on the book uses the book row the board already loaded. A company off the book
// gets the extension's record lookup and the Request button. Detail sits in hover titles so the
// panel stays a few dense lines tall.

import { useQuery } from '@tanstack/react-query';
import { fetchExtensionRecord, panelMessage } from '../api/client';
import type { BookAccount, BookPayload, EnrichCandidate, ExtensionRecord, Viewer } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { fundShortName, readFunding, readRelationship } from '../book/funding';
import { formatCredits } from '../lib/format';
import { Fact, Separated, SideBlock } from '../sidebar/blocks';
import { CompanyTab } from '../sidebar/CompanyTab';
import { HistoryTab } from '../sidebar/HistoryTab';
import { OutreachTab } from '../sidebar/OutreachTab';
import { ResearchTab } from '../sidebar/ResearchTab';
import { TOOL_SITE_LINE } from './pageContext.ts';
import type { CompanyHint } from './pageContext.ts';
import { RequestRoute } from './RequestRoute';

/** The off-book record for a domain. Shared by the strip and the Company section (one fetch). */
function useExtensionRecord(domain: string | null) {
  const { getToken } = useAuthContext();
  return useQuery({
    queryKey: ['extension-record', domain],
    queryFn: () => fetchExtensionRecord(getToken, domain || ''),
    enabled: Boolean(domain),
  });
}

// The funding label, with the round detail, the source and our funds on hover.
function fundingParts(row: BookAccount): { label: string; title: string } {
  const funding = readFunding(row);
  const relationship = readRelationship(row);
  const funds = relationship.kind === 'funds'
    ? `Our funds: ${relationship.ties.map(tie => fundShortName(tie.fund)).join(', ')}`
    : '';
  return { label: funding.label, title: [funding.detail, funds, funding.hint].filter(Boolean).join('\n') };
}

function recordTitle(record: ExtensionRecord): string {
  return [
    record.owner_email ? `Owner: ${record.owner_email}` : '',
    `${record.opportunity_count} opportunities`,
    `30-day credits: ${formatCredits(record.credits_30)}`,
    record.status ? `Status: ${record.status}` : '',
  ].filter(Boolean).join('\n');
}

/** Name, domain, strategist and funding in two lines, with Request / Route at the right. */
export function AccountStrip({ row, hint, viewer, toolSite }: {
  row: BookAccount | null; hint: CompanyHint; viewer: Viewer; toolSite: boolean;
}) {
  const recordQuery = useExtensionRecord(row ? null : hint.domain);

  if (row) {
    const funding = fundingParts(row);
    const onMyBook = (row.sdr_owner_email || '').toLowerCase() === (viewer.email || '').toLowerCase();
    return (
      <div className="extstrip">
        <div className="extstripmain">
          <h2 title={row.account}>{row.account}</h2>
          <p className="sidesub">
            <Separated parts={[
              row.domain,
              <span key="owner" title="Strategist">{row.sdr_owner || 'Unassigned'}</span>,
              <span key="funding" className="exthover" title={funding.title || undefined}>{funding.label}</span>,
            ]} />
          </p>
        </div>
        <RequestRoute viewer={viewer} domain={row.domain || ''} name={row.account} accountId={row.id}
          onMyBook={onMyBook} excludedWhy={null} />
      </div>
    );
  }

  // A tool's own site (Amplemarket, Salesforce): a quiet line, no lookup and no Request.
  if (toolSite) {
    return (
      <div className="extstrip">
        <div className="extstripmain">
          <h2>No company on this page</h2>
          <p className="sidesub">{TOOL_SITE_LINE}.</p>
        </div>
      </div>
    );
  }

  if (!hint.domain) {
    return (
      <div className="extstrip">
        <div className="extstripmain">
          <h2>{hint.name || 'No company on this page'}</h2>
          <p className="sidesub">{hint.name ? 'Not on the book. Type its website above.' : 'Type a name or website above.'}</p>
        </div>
      </div>
    );
  }

  const record = recordQuery.data;
  return (
    <div className="extstrip">
      <div className="extstripmain">
        <h2 title={record?.name || hint.name || hint.domain}>{record?.name || hint.name || hint.domain}</h2>
        <p className="sidesub">
          <Separated parts={[
            hint.domain,
            recordQuery.isPending ? 'Looking up…' : null,
            record ? (
              <span key="record" className="exthover" title={recordTitle(record)}>
                {record.found ? (record.owner ? `Salesforce: ${record.owner}` : 'Salesforce: no owner') : 'Not in Salesforce'}
              </span>
            ) : null,
            record && !record.on_book ? 'Off the book' : null,
          ]} />
        </p>
        {recordQuery.error ? <p className="extnote bad">{panelMessage(recordQuery.error, 'Could not look up this company.')}</p> : null}
      </div>
      {record ? (
        <RequestRoute viewer={viewer} domain={hint.domain} name={record.name || hint.name}
          accountId={record.account_id} onMyBook={record.on_my_book} excludedWhy={record.excluded_why} />
      ) : null}
    </div>
  );
}

/** Company: best signal, overview, ownership and activity. Off the book: the Salesforce record. */
export function CompanySection({ row, hint }: { row: BookAccount | null; hint: CompanyHint }) {
  const recordQuery = useExtensionRecord(row ? null : hint.domain);
  if (!row) {
    const record = recordQuery.data;
    if (!record) {
      if (recordQuery.isPending && hint.domain) return <p className="tabempty">Looking up {hint.domain}…</p>;
      return <p className="tabempty">{hint.domain ? 'No Salesforce record to show.' : 'Type a company name or website above.'}</p>;
    }
    return (
      <SideBlock heading="Salesforce">
        {record.found ? (
          <div className="facts">
            <Fact label="Owner" value={record.owner || 'None'} />
            <Fact label="Opportunities" value={String(record.opportunity_count)} />
            <Fact label="30-day credits" value={formatCredits(record.credits_30)} />
            <Fact label="Status" value={record.status} />
          </div>
        ) : <p className="prose">{record.message || 'Not in Salesforce.'}</p>}
      </SideBlock>
    );
  }

  return <CompanyTab row={row} />;
}

/** Outreach for a book account: the sidebar's Outreach tab. */
export function OutreachAccount({ row, book, active, onOpenScout, onContactEnriched }: {
  row: BookAccount;
  book: BookPayload;
  active: boolean;
  onOpenScout: () => void;
  onContactEnriched: (contactId: string, candidate: EnrichCandidate) => void;
}) {
  const { getToken } = useAuthContext();
  return (
    <OutreachTab row={row} book={book} getToken={getToken} active={active} onContactEnriched={onContactEnriched}
      onOpenScout={onOpenScout} />
  );
}

/** Research, then History: the sidebar's two tabs, one under the other. */
export function ResearchSection({ row, today }: { row: BookAccount; today: string }) {
  return (
    <>
      <ResearchTab row={row} today={today} workReason={null} />
      <HistoryTab row={row} />
    </>
  );
}
